import { applyAction, type Action } from "../engine.js";
import type { Brain, BrainInput, BrainOutput } from "../room.js";
import { FALLBACK_LINES } from "../room.js";
import type { GameState } from "../types.js";
import { contextPrompt, staticPrompt } from "./prompt.js";
import { toolCallToAction, type ToolCall } from "./toolcalls.js";
import { TOOLS, type ToolDef } from "./tools.js";

export type ChatTurn =
  | { role: "user"; text: string }
  | { role: "assistant"; text: string; toolCalls: ToolCall[]; raw?: unknown }
  | { role: "tool_results"; results: { id: string; content: string; isError: boolean }[] };

export interface LLMRequest {
  /** [static (cacheable), dynamic context]. */
  system: [string, string];
  turns: ChatTurn[];
  tools: readonly ToolDef[];
  /** Only the rule-based mock reads this; real providers ignore it. */
  meta?: { state: GameState; now: number };
}

export interface LLMResponse {
  text: string;
  toolCalls: ToolCall[];
  usage: { inputTokens: number; outputTokens: number };
  stop: "end" | "tool_use" | "max_tokens" | "refusal" | "other";
  /** Provider-native assistant content, echoed back unchanged on the follow-up call. */
  raw?: unknown;
}

/** SPEC 7: one interface for every model provider. */
export interface LLMProvider {
  readonly name: string;
  chat(req: LLMRequest): Promise<LLMResponse>;
}

const MAX_HISTORY = 12;

/** History for the model: last messages, starting with a user turn. */
export function historyTurns(state: GameState): ChatTurn[] {
  const turns: ChatTurn[] = state.messages
    .slice(-MAX_HISTORY)
    .map((m) =>
      m.role === "user" ? { role: "user", text: m.text } : { role: "assistant", text: m.text, toolCalls: [] },
    );
  // The companion often speaks first (brief, nudge); APIs want a user turn first.
  if (turns[0]?.role === "assistant") turns.unshift({ role: "user", text: "(podchodzę do ekranu)" });
  return turns;
}

/** Speakable reply: no markdown/emoji/amounts, about 35 words max. */
export function sanitizeReply(text: string): string {
  let t = text
    .replace(/[*_#`>]+/g, "")
    .replace(/\p{Extended_Pictographic}/gu, "")
    .replace(/^\s*[-•]\s*/gm, "")
    .replace(/\s+/g, " ")
    .trim();
  // Never say or show real money amounts (the tablet hangs where guests see it).
  t = t
    .replace(/\d[\d\s.,]*\s*(zł|złotych|zl|pln|k\b|tys\.?|tysi\w*|euro|€|\$)/gi, "")
    .replace(/\s+([,.!?])/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();
  const words = t.split(" ");
  if (words.length > 40) {
    const sentences = t.match(/[^.!?]+[.!?]+/g) ?? [t];
    t = sentences.slice(0, 2).join(" ").trim();
    if (t.split(" ").length > 40) t = words.slice(0, 35).join(" ") + "…";
  }
  return t;
}

export interface TurnTrace {
  calls: ToolCall[];
  actions: Action[];
  results: { name: string; ok: boolean; result: string }[];
  rounds: number;
}

/**
 * Conversation brain on top of any LLMProvider: one call normally,
 * a second one only when the model returned no text or a tool failed.
 */
export class LLMBrain implements Brain {
  lastTrace: TurnTrace | null = null;

  constructor(
    private readonly provider: LLMProvider,
    private readonly opts: { maxRounds?: number } = {},
  ) {}

  async respond({ state, now }: BrainInput): Promise<BrainOutput> {
    const usage = { inputTokens: 0, outputTokens: 0, calls: 0 };
    const trace: TurnTrace = { calls: [], actions: [], results: [], rounds: 0 };
    this.lastTrace = trace;
    const system: [string, string] = [staticPrompt(state), contextPrompt(state, now)];
    const turns = historyTurns(state);
    let sim = state;
    let text = "";
    const maxRounds = this.opts.maxRounds ?? 2;
    try {
      for (let round = 0; round < maxRounds; round++) {
        const r = await this.provider.chat({ system, turns, tools: TOOLS, meta: { state: sim, now } });
        usage.calls++;
        usage.inputTokens += r.usage.inputTokens;
        usage.outputTokens += r.usage.outputTokens;
        trace.rounds++;
        if (r.stop === "refusal") break;
        if (r.text.trim()) text = r.text;
        if (!r.toolCalls.length) break;
        const results: { id: string; content: string; isError: boolean }[] = [];
        let failed = false;
        for (const call of r.toolCalls) {
          trace.calls.push(call);
          const mapped = toolCallToAction(call, sim, now);
          if ("error" in mapped) {
            failed = true;
            results.push({ id: call.id, content: `error: ${mapped.error}`, isError: true });
            trace.results.push({ name: call.name, ok: false, result: mapped.error });
            continue;
          }
          // Simulate on a copy so the model gets real results (e.g. not enough gold).
          const applied = applyAction(sim, mapped.action, now);
          trace.results.push({ name: call.name, ok: applied.ok, result: applied.result });
          results.push({
            id: call.id,
            content: applied.ok ? `ok: ${applied.result}` : `error: ${applied.result}`,
            isError: !applied.ok,
          });
          if (applied.ok) {
            sim = applied.state;
            trace.actions.push(mapped.action);
          } else failed = true;
        }
        if (r.text.trim() && !failed) break;
        turns.push({ role: "assistant", text: r.text, toolCalls: r.toolCalls, raw: r.raw });
        turns.push({ role: "tool_results", results });
      }
    } catch (e) {
      return { text: FALLBACK_LINES.error, actions: [], usage, error: String((e as Error)?.message ?? e) };
    }
    const reply =
      sanitizeReply(text) || (trace.actions.length ? "Zapisane." : "Hmm, powiesz to jeszcze raz?");
    return { text: reply, actions: trace.actions, usage };
  }
}
